import type { ReactNode } from "react"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link, useLocation, useNavigate } from "@tanstack/react-router"
import { IconSidebarEditFill18 } from "nucleo-ui-fill-18"
import {
	IconAtom2Outline18,
	IconBookOpen4Outline18,
	IconChart3Outline18,
	IconChevronDownOutline18,
	IconDotsOutline18,
	IconInboxOutline18,
	IconLifeRingOutline18,
	IconLightbulbOutline18,
	IconStackPerspectiveOutline18,
	IconNotificationOutline18,
	IconOfficeOutline18,
	IconSidebarLeftOutline18 as PanelLeft,
} from "nucleo-ui-outline-18"

import type {
	SidebarBadgeStyle,
	SidebarItemId,
} from "@productlane/lib/src/sidebar-layout"
import type { IconType } from "@productlane/ui"
import { cn } from "@productlane/lib/src/browser"
import {
	INBOX_TAB_ITEM_IDS,
	inboxTabForItem,
} from "@productlane/lib/src/sidebar-layout"
import {
	Avatar,
	Badge,
	Button,
	IconButton,
	Shortcut,
	Tooltip,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from "@productlane/ui/src/components/internal/dropdown"
import { TooltipProvider } from "@productlane/ui/src/components/internal/tooltip"
import { queries } from "@productlane/zero/queries"

import type { SidebarEntry, SidebarItemOverrides } from "./sidebar"
import type { InboxState } from "@/lib/states/global-state"
import { WIDGET_CONTAINER_SELECTOR } from "@/components/ai-widget-launcher"
import { OutOfOfficeReturnBanner } from "@/components/out-of-office/out-of-office-return-banner"
import {
	PANEL_SPECS,
	panelWidthStyle,
} from "@/components/panel-resize/panel-resize"
import { PanelResizeGutter } from "@/components/panel-resize/panel-resize-handle"
import {
	NavSectionBody,
	NavSectionHeader,
} from "@/components/shared/nav-section-header"
import { useShortcut } from "@/lib"
import { pressToActivate, useLinkPress } from "@/lib/press-to-navigate"
import { useSession } from "@/lib/session"
import { useThreadState } from "@/lib/states/global-state"
import {
	INBOX_SECTIONS,
	useIsInboxSectionOpen,
} from "@/lib/states/inbox-nav-state"
import { useMainNavState } from "@/lib/states/main-nav-state"
import { usePanelWidthState } from "@/lib/states/panel-width-state"
import { useTrial } from "@/lib/useTrial"
import { useZeroWorkspace } from "@/lib/zero"
import { isRecommendationsPath } from "./agent/agent-route"
import { useIsAgentSidebarShown } from "./agent/agent-sidebar-section"
import { ChecklistContent } from "./checklist"
import {
	activeCombinedNavIndex,
	buildCombinedNavStops,
	combinedNavOrder,
	combinedSidebarOmit,
	entryRoutes,
	isRouteActive,
	nextCombinedNavIndex,
} from "./combined-sidebar-routes"
import { CustomViewsList } from "./inbox/components/custom-views-list"
import {
	INBOX_NAV_COUNT,
	INBOX_NAV_GROUP,
	INBOX_NAV_ICON,
	INBOX_NAV_LEAD,
	INBOX_NAV_ROW,
	INBOX_NAV_ROW_ACTIVE,
	INBOX_NAV_ROW_INACTIVE,
	INBOX_NAV_SECTION_HEADER,
} from "./inbox/components/inbox-nav-styles"
import { useInboxNavTabs } from "./inbox/helpers/use-inbox-nav-tabs"
import { getFormattedThreadCount } from "./inbox/useThreadCount"
import { inboxTabShortcut } from "./navigation-shortcuts"
import { mentionDotClass } from "./notifications/notification-icons"
import { useUnreadMentions } from "./notifications/use-notifications"
import {
	CountDot,
	CustomizeSidebarHost,
	NavShortcutLabel,
	SelectWorkspace,
	useFocusSearchShortcut,
	useSidebarEntries,
} from "./sidebar"
import {
	HIDDEN_NAV_TOGGLE_POSITION,
	mainNavFrame,
	mainNavFrameStyles,
	NAV_COLUMN_TRANSITION,
	SIDEBAR_HIDDEN_GUTTER_WIDTH,
} from "./sidebar-geometry"
import { SIDEBAR_LAYOUT_COPY } from "./sidebar-layout/copy"
import { SidebarContextMenu } from "./sidebar-layout/sidebar-context-menu"
import {
	useCustomizeSidebarState,
	useSidebarSectionFoldState,
} from "./sidebar-layout/sidebar-layout-state"
import { AiResolutionCapBanner } from "./threads/components/sidebar-warnings/ai-resolution-cap"
import { LinearReconnectRequiredBanner } from "./threads/components/sidebar-warnings/linear-revoked"
import { PaymentFailedBanner } from "./threads/components/sidebar-warnings/payment-failed-banner"
import { TrialBanner } from "./threads/components/sidebar-warnings/trial-banner"
import { ViewAccessWarning } from "./threads/components/sidebar-warnings/view-access"

type Icon = IconType

const PEEK_CLOSE_GRACE_MS = 100
// With no skip window, each row waits the full delay, so sweeping the list never flashes tooltips.
const NAV_TOOLTIP_DELAY_MS = 500

// The rail's filled marks read heavy at row size, so the sidebar draws the outline cut of each.
export const ROW_ICONS: Partial<Record<SidebarItemId, Icon>> = {
	inbox: IconInboxOutline18,
	notifications: IconNotificationOutline18,
	recommendations: IconAtom2Outline18,
	reporting: IconChart3Outline18,
	docs: IconBookOpen4Outline18,
	"feature-requests": IconLightbulbOutline18,
	"support-portal": IconLifeRingOutline18,
	changelog: IconStackPerspectiveOutline18,
	customers: IconOfficeOutline18,
}

const ROW_ICON_CLASS_NAMES: Partial<Record<SidebarItemId, string>> = {
	"inbox-sla-breached": "text-red-500",
}

function rowBadge(
	style: SidebarBadgeStyle,
	count: number | undefined,
	label: string | undefined,
): ReactNode {
	if (!count || count <= 0 || style === "off") return null
	if (style === "dot") return <CountDot count={count} className="h-4 w-4" />
	return (
		<Badge variant="transparent" size="xs" className={INBOX_NAV_COUNT}>
			{label ?? (count > 99 ? "99+" : count)}
		</Badge>
	)
}

const iconFor = (entry: SidebarEntry): Icon =>
	ROW_ICONS[entry.id] ?? (entry.item.icon as Icon)

/** A page row is a link; an inbox tab row is a button, since a tab is state on /threads. */
const NavRow = memo(
	({
		href,
		onSelect,
		icon: RowIcon,
		iconClassName,
		label,
		trailing,
		active,
		shortcut,
	}: {
		href?: string
		onSelect?: () => void
		icon: Icon
		iconClassName?: string
		label: string
		trailing?: ReactNode
		active: boolean
		shortcut?: Array<string>
	}) => {
		const linkPress = useLinkPress(href ?? "")
		const className = cn(
			INBOX_NAV_ROW,
			"w-full",
			active ? INBOX_NAV_ROW_ACTIVE : INBOX_NAV_ROW_INACTIVE,
		)
		const content = (
			<>
				<div className={INBOX_NAV_LEAD}>
					<RowIcon
						strokeWidth={1.25}
						className={cn(INBOX_NAV_ICON, iconClassName)}
					/>
					<span className="truncate">{label}</span>
				</div>
				{trailing}
			</>
		)
		const row = href ? (
			<Link to={href} className={className} {...linkPress}>
				{content}
			</Link>
		) : (
			<button
				type="button"
				className={className}
				{...pressToActivate(() => onSelect?.())}
			>
				{content}
			</button>
		)
		if (!shortcut) return row
		return (
			<Tooltip
				side="right"
				label={<NavShortcutLabel title={label} shortcut={shortcut} />}
			>
				{row}
			</Tooltip>
		)
	},
)

const WorkspaceHeader = memo(() => {
	const workspace = useZeroWorkspace()
	const { session } = useSession()
	const workspaceName = workspace?.name ?? session?.workspaceName
	const {
		hidden,
		toggle,
		workspaceMenuOpen: open,
		setWorkspaceMenuOpen: setOpen,
	} = useMainNavState()
	// No search button here, so / is the only way in.
	useFocusSearchShortcut()

	return (
		<div className="flex h-12 flex-none items-center gap-0.5 pl-1 pr-1.5">
			{/* The hover area hugs the name, like Linear's, instead of spanning the row. */}
			<div className="flex min-w-0 flex-1">
				<SelectWorkspace isOpen={open} onOpenChange={setOpen}>
					<Button
						variant="ghost"
						className={cn(
							"group/workspace h-7 max-w-full rounded-lg pl-1.5 pr-1.5 text-primary hover:bg-gray-150 hover:dark:bg-gray-800",
							open && "bg-gray-150 dark:bg-gray-800",
						)}
						wrapperClassName="flex min-w-0 max-w-full"
					>
						<div className="flex min-w-0 items-center gap-1.5">
							<Avatar
								image={workspace?.logoUrl ?? session?.workspaceLogoUrl}
								name={workspaceName || "Productlane"}
								size="xs"
								roundness="md"
								disableColorHash
								fallbackProps={{
									className:
										"bg-gray-950 text-white dark:bg-gray-50 dark:text-gray-950",
								}}
							/>
							<div className="min-w-0 truncate text-left text-[0.875rem] font-semibold">
								{workspaceName}
							</div>
							{/* Kept in the layout and only faded, so the name never shifts on hover. */}
							<IconChevronDownOutline18
								strokeWidth={1.75}
								className={cn(
									"h-3 w-3 flex-none transform-gpu text-secondary",
									open
										? "opacity-100"
										: "opacity-0 group-hover/workspace:opacity-100",
								)}
							/>
						</div>
					</Button>
				</SelectWorkspace>
			</div>
			{/* While hidden, the floating toggle docks instead. */}
			{!hidden && (
				<Tooltip
					label={
						<div className="flex items-center gap-1">
							Hide sidebar
							<Shortcut>[</Shortcut>
						</div>
					}
				>
					<IconButton
						variant="ghost"
						Icon={PanelLeft}
						iconStrokeWidth={1.25}
						aria-label="Hide sidebar"
						onClick={toggle}
						className="flex-none transform-gpu"
					/>
				</Tooltip>
			)}
		</div>
	)
})

const MoreMenu = memo(
	({
		entries,
		onSelectEntry,
	}: {
		entries: SidebarEntry[]
		onSelectEntry: (entry: SidebarEntry) => void
	}) => {
		const [open, setOpen] = useState(false)
		const setCustomizeOpen = useCustomizeSidebarState((state) => state.setOpen)
		return (
			<DropdownMenu
				open={open}
				onOpenChange={setOpen}
				contentProps={{ side: "bottom", align: "start" }}
				className="w-[210px]"
				trigger={
					<button
						type="button"
						data-testid="combined-sidebar-more"
						className={cn(
							INBOX_NAV_ROW,
							"w-full",
							open ? INBOX_NAV_ROW_ACTIVE : INBOX_NAV_ROW_INACTIVE,
						)}
					>
						<div className={INBOX_NAV_LEAD}>
							<IconDotsOutline18
								strokeWidth={1.25}
								className={INBOX_NAV_ICON}
							/>
							<span className="truncate">{SIDEBAR_LAYOUT_COPY.more}</span>
						</div>
					</button>
				}
			>
				{entries.length > 0 && (
					<>
						<DropdownMenuGroup>
							{entries.map((entry) => (
								<DropdownMenuItem
									key={entry.id}
									Icon={iconFor(entry)}
									iconStrokeWidth={1.25}
									onSelect={() => onSelectEntry(entry)}
								>
									{entry.item.title}
								</DropdownMenuItem>
							))}
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
					</>
				)}
				<DropdownMenuItem
					Icon={IconSidebarEditFill18}
					onSelect={() => setCustomizeOpen(true)}
				>
					{SIDEBAR_LAYOUT_COPY.dialogTitle}
				</DropdownMenuItem>
			</DropdownMenu>
		)
	},
)

/**
 * The slot stays mounted and keeps its last row while it closes, so the row
 * slides in above More and back out instead of popping.
 */
const PromotedSlot = memo(
	({
		entry,
		renderRow,
	}: {
		entry: SidebarEntry | undefined
		renderRow: (entry: SidebarEntry, active: boolean) => ReactNode
	}) => {
		const [lastEntry, setLastEntry] = useState(entry)
		useEffect(() => {
			if (entry) setLastEntry(entry)
		}, [entry])
		const open = !!entry
		const row = entry ?? lastEntry

		return (
			<div
				data-testid="combined-sidebar-promoted"
				data-open={open}
				inert={!open}
				aria-hidden={!open}
				className={cn(
					"ease-[cubic-bezier(0.23,1,0.32,1)] grid transition-[grid-template-rows,opacity]",
					open
						? "grid-rows-[1fr] opacity-100 duration-200"
						: "grid-rows-[0fr] opacity-0 duration-150",
				)}
			>
				<div className="min-h-0 overflow-hidden">
					<div
						className={cn(
							"ease-[cubic-bezier(0.23,1,0.32,1)] pb-px transition-transform",
							open
								? "translate-y-0 duration-200"
								: "-translate-y-1 duration-150",
						)}
					>
						{row && renderRow(row, open)}
					</div>
				</div>
			</div>
		)
	},
)

/** Rail and inbox panel folded into one column, Linear-style. Productlane workspace only for now. */
export const CombinedSidebar = memo(() => {
	const { pathname } = useLocation()
	const navigate = useNavigate()
	const { isTrial, daysLeft, hasPaymentMethod, paymentIssue } = useTrial()
	const {
		inboxState,
		setInboxState,
		activeCustomViewId,
		setActiveCustomViewId,
	} = useThreadState()
	const { tabs } = useInboxNavTabs(inboxState, { everyTab: true })
	const agentShown = useIsAgentSidebarShown()

	// Each inbox tab is its own item here, so it can be reordered, hidden, or shown only when badged.
	const overrides = useMemo<SidebarItemOverrides>(
		() => ({
			omit: combinedSidebarOmit(agentShown),
			items: {
				...Object.fromEntries(
					tabs.map((tab) => [
						INBOX_TAB_ITEM_IDS[tab.value as keyof typeof INBOX_TAB_ITEM_IDS],
						{
							href: "/threads",
							title: tab.label,
							icon: tab.icon as Icon,
							count: tab.count,
							countLabel:
								tab.count !== undefined
									? getFormattedThreadCount(tab.count)
									: undefined,
							shortcut: inboxTabShortcut(tab.value),
						},
					]),
				),
			},
		}),
		[tabs, agentShown],
	)
	const {
		layout,
		personalShown,
		inboxShown,
		workspaceEntries,
		workspaceShown,
		hiddenEntries,
		moreEntries: contextMenuEntries,
	} = useSidebarEntries(overrides)
	const { count: unreadMentions } = useUnreadMentions()
	const navWidth = usePanelWidthState((state) => state.widths["main-nav"])
	const columnRef = useRef<HTMLDivElement>(null)
	const {
		hidden,
		toggle: toggleHidden,
		peek: hoverPeek,
		setPeek,
		setLauncherHidden,
		workspaceMenuOpen,
	} = useMainNavState()
	// The menu anchors to the header, so a hidden nav slides in while it is open.
	const peek = hoverPeek || workspaceMenuOpen
	// Set by a peek, cleared once it has slid away or on the next hide, so no slide swaps frames midway.
	const [floating, setFloating] = useState(false)
	const [wasHidden, setWasHidden] = useState(hidden)
	if (hidden !== wasHidden) {
		setWasHidden(hidden)
		if (hidden) setFloating(false)
	}
	if (hidden && peek && !floating) setFloating(true)
	const frame = mainNavFrame({ hidden, peek, floating })
	const peekTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
	useEffect(() => () => clearTimeout(peekTimer.current), [])
	useShortcut("bracketleft", toggleHidden, { preventDefault: true })
	// The grace lets the pointer cross onto the launcher, whose iframe sits outside this tree.
	const closePeek = useCallback(() => {
		clearTimeout(peekTimer.current)
		peekTimer.current = setTimeout(() => setPeek(false), PEEK_CLOSE_GRACE_MS)
	}, [setPeek])

	const dock = () => {
		clearTimeout(peekTimer.current)
		toggleHidden()
	}

	const launcherHidden = hidden && !peek
	useEffect(() => {
		setLauncherHidden(launcherHidden)
	}, [launcherHidden, setLauncherHidden])
	useEffect(() => () => setLauncherHidden(false), [setLauncherHidden])

	useEffect(() => {
		if (!hidden || !peek) return
		const launcher = document.querySelector(WIDGET_CONTAINER_SELECTOR)
		if (!launcher) return
		const keepPeek = () => clearTimeout(peekTimer.current)
		launcher.addEventListener("mouseenter", keepPeek)
		launcher.addEventListener("mouseleave", closePeek)
		return () => {
			launcher.removeEventListener("mouseenter", keepPeek)
			launcher.removeEventListener("mouseleave", closePeek)
		}
	}, [hidden, peek, closePeek])

	const onThreads = pathname.startsWith("/threads")

	const selectTab = useCallback(
		(tab: InboxState) => {
			setInboxState(tab)
			setActiveCustomViewId(null)
			void navigate({ to: "/threads" })
		},
		[navigate, setActiveCustomViewId, setInboxState],
	)

	const isEntryActive = (entry: SidebarEntry) => {
		if (entry.id === "recommendations") return isRecommendationsPath(pathname)
		const tab = inboxTabForItem(entry.id)
		if (tab) return onThreads && !activeCustomViewId && inboxState === tab
		return isRouteActive(pathname, entryRoutes(entry))
	}
	const selectEntry = (entry: SidebarEntry) => {
		const tab = inboxTabForItem(entry.id)
		if (tab) selectTab(tab as InboxState)
		else void navigate({ href: entry.item.href })
	}

	// Unpublished pages have no Try section here, so they wait in More with the hidden ones.
	const moreEntries = [
		...hiddenEntries,
		...workspaceEntries.filter(
			(entry) => entry.unpublished && layout.visibility[entry.id] !== "hidden",
		),
	]
	const promoted = moreEntries.find(isEntryActive)
	const groups = combinedNavOrder(layout.combined, [
		...personalShown,
		...inboxShown,
		...workspaceShown,
	])
	const workspaceFolded = useSidebarSectionFoldState(
		(state) => !!state.folded.workspace,
	)
	const toggleSectionFold = useSidebarSectionFoldState((state) => state.toggle)

	const [customViews] = useQuery(queries.customViews())
	const viewsOpen = useIsInboxSectionOpen(INBOX_SECTIONS.views)
	const stops = buildCombinedNavStops({
		// A folded group is off screen, so the keys skip it like the eye does.
		entries: workspaceFolded
			? groups.top
			: [...groups.top, ...groups.workspace],
		promoted: workspaceFolded ? undefined : promoted,
		viewIds: viewsOpen ? customViews.map((view) => view.id) : [],
	})
	const activeIndex = activeCombinedNavIndex(stops, {
		pathname,
		inboxState,
		activeCustomViewId,
	})

	const goToStop = (index: number) => {
		const stop = stops[index]
		if (!stop) return
		if (stop.kind === "route" || stop.kind === "agent")
			void navigate({ href: stop.href })
		else if (stop.kind === "tab") selectTab(stop.value)
		else {
			setActiveCustomViewId(stop.id)
			setInboxState("custom-view")
			void navigate({ to: "/threads" })
		}
	}
	// Only while a nav page is open, so Tab still moves focus on settings and other screens.
	const navKeysEnabled = activeIndex !== -1
	useShortcut(
		"tab",
		() => goToStop(nextCombinedNavIndex(stops.length, activeIndex, "forward")),
		{ preventDefault: true, enabled: navKeysEnabled },
	)
	useShortcut(
		"shift+tab",
		() => goToStop(nextCombinedNavIndex(stops.length, activeIndex, "backward")),
		{ preventDefault: true, enabled: navKeysEnabled },
	)

	const renderRow = (entry: SidebarEntry, active: boolean) => {
		const isTab = !!inboxTabForItem(entry.id)
		return (
			<NavRow
				key={entry.id}
				href={isTab ? undefined : entry.item.href}
				onSelect={isTab ? () => selectEntry(entry) : undefined}
				icon={iconFor(entry)}
				iconClassName={cn(
					ROW_ICON_CLASS_NAMES[entry.id],
					entry.id === "notifications" && mentionDotClass(unreadMentions),
				)}
				label={entry.item.title}
				trailing={rowBadge(
					layout.badgeStyle,
					entry.item.count,
					entry.item.countLabel,
				)}
				active={active}
				shortcut={entry.item.shortcut}
			/>
		)
	}

	return (
		<>
			{/* Hidden, the column shrinks to a gutter; hovering the gutter slides the same nav in as an overlay. */}
			<div
				ref={columnRef}
				style={panelWidthStyle(PANEL_SPECS["main-nav"], navWidth)}
				className={cn(
					"relative h-dvh flex-none",
					NAV_COLUMN_TRANSITION,
					// Docked, the card paints over the nav while it slides; only a peek floats above the page.
					hidden
						? cn("z-40", SIDEBAR_HIDDEN_GUTTER_WIDTH, "min-w-2")
						: "isolate w-[var(--main-nav-width,240px)] min-w-[var(--main-nav-width,240px)]",
				)}
				onMouseEnter={() => {
					if (!hidden) return
					clearTimeout(peekTimer.current)
					peekTimer.current = setTimeout(() => setPeek(true), 150)
				}}
				onMouseLeave={() => {
					if (hidden) closePeek()
				}}
			>
				<SidebarContextMenu
					hiddenItems={contextMenuEntries.filter(
						(item) => item.href !== "/threads",
					)}
				>
					<nav
						aria-label="Main"
						translate="no"
						data-hidden={hidden}
						onTransitionEnd={(event) => {
							if (
								frame === "unpeeked" &&
								event.target === event.currentTarget &&
								event.propertyName === "transform"
							)
								setFloating(false)
						}}
						className={cn(
							"flex w-[var(--main-nav-width,240px)] select-none flex-col p-1 lg:py-2",
							mainNavFrameStyles(frame),
						)}
					>
						<WorkspaceHeader />
						<TooltipProvider
							delayDuration={NAV_TOOLTIP_DELAY_MS}
							skipDelayDuration={0}
						>
							<div className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-1.5 pb-3 pt-1">
								<div className={INBOX_NAV_GROUP}>
									{groups.top.map((entry) =>
										renderRow(entry, isEntryActive(entry)),
									)}
								</div>
								{/* Same header as Views: the chevron waits for hover while the group is open. */}
								<div
									className={cn("group/inbox-section mt-4", INBOX_NAV_GROUP)}
								>
									<NavSectionHeader
										label={SIDEBAR_LAYOUT_COPY.sections.workspace}
										hoverGroup="inbox-section"
										isOpen={!workspaceFolded}
										onToggle={() => toggleSectionFold("workspace")}
										className={INBOX_NAV_SECTION_HEADER}
									/>
									<NavSectionBody isOpen={!workspaceFolded}>
										<div className={INBOX_NAV_GROUP}>
											{groups.workspace.map((entry) =>
												renderRow(entry, isEntryActive(entry)),
											)}
											<PromotedSlot entry={promoted} renderRow={renderRow} />
											<MoreMenu
												entries={moreEntries}
												onSelectEntry={selectEntry}
											/>
										</div>
									</NavSectionBody>
								</div>
								<CustomViewsList />
							</div>
						</TooltipProvider>
						<div className="flex shrink-0 flex-col pb-11">
							<LinearReconnectRequiredBanner />
							<AiResolutionCapBanner />
							<ViewAccessWarning />
							<ChecklistContent />
							<OutOfOfficeReturnBanner />
							{isTrial && !!daysLeft && !hasPaymentMethod && (
								<TrialBanner daysLeft={daysLeft} />
							)}
							{!!paymentIssue && <PaymentFailedBanner issue={paymentIssue} />}
						</div>
					</nav>
				</SidebarContextMenu>
				{/* Hovering it peeks the nav, which opens just below it, like Linear's. */}
				<Tooltip
					label={
						<div className="flex items-center gap-1">
							Dock sidebar
							<Shortcut>[</Shortcut>
						</div>
					}
				>
					<IconButton
						variant="ghost"
						Icon={PanelLeft}
						iconStrokeWidth={1.25}
						aria-label="Dock sidebar"
						onClick={dock}
						className={cn(
							HIDDEN_NAV_TOGGLE_POSITION,
							"z-10 transform-gpu",
							// Fades in once the nav has slid away; leaves at once on dock.
							hidden
								? "visible opacity-100 transition-[opacity,visibility] delay-200 duration-150"
								: "invisible opacity-0",
						)}
					/>
				</Tooltip>
			</div>
			{!hidden && (
				<PanelResizeGutter
					panel="main-nav"
					label="Resize sidebar"
					// The column owns the width var and its transition; dragging the nav alone left rows hanging over the content.
					getPanel={() => columnRef.current}
				/>
			)}
			{/* Outside the menu's trigger: portal events bubble through the React tree. */}
			<CustomizeSidebarHost
				overrides={overrides}
				variant="combined"
				icons={ROW_ICONS}
				iconClassNames={ROW_ICON_CLASS_NAMES}
			/>
		</>
	)
})
